<script setup lang="ts">
import type {
  FindManyFilter,
  FindManyFilterItem,
  FindManyFilterItemV2,
  FindManyFilterV2,
} from '@rialtic/types'
import { computed, ref } from '#imports'
import type { FilterItemWithState } from '../../composables/filters'
import useSimpleSearch from '../../composables/useSimpleSearch'
import Button from '../Button.vue'
import FloatingUi from '../FloatingUi.vue'
import ListItemCheckbox from '../list/ItemCheckbox.vue'
import ListItemRadio from '../list/ItemRadio.vue'
import Search from '../Search.vue'
import FilterChip from './Chip.vue'

const props = defineProps<{
  filter: FindManyFilter | FindManyFilterV2
  search?: boolean
  selectAllDisabled?: boolean
  clearAllDisabled?: boolean
  dataAnalyticsTagMap?: Record<string, string>
}>()

const emit = defineEmits([
  'filter-all',
  'filter-change',
  'filter-clear',
  'error',
])

const activator = ref(null)
const open = ref(false)
const searchInput = ref('')

const filteredItems = useSimpleSearch<
  FilterItemWithState<FindManyFilterItem | FindManyFilterItemV2>
>(
  computed(() => props.filter.items || []),
  searchInput,
  'label',
)

const activeFilterLabels = computed(() => {
  return (props.filter.items as FilterItemWithState[])
    .filter((filterItem) => filterItem.active)
    .map((i) => i.label)
})

const setOpen = (isOpen = false) => {
  open.value = isOpen
}
</script>

<template>
  <div class="relative inline-block">
    <div ref="activator">
      <slot v-bind="{ open, setOpen }" name="activator">
        <FilterChip
          v-model="activeFilterLabels"
          class="h-full"
          :label="filter.label"
          button-border-radius="rounded-lg"
          @click="setOpen(true)"
        />
      </slot>
    </div>
    <FloatingUi
      v-if="open"
      class="bg-surface shadow-lg"
      :anchor="activator!"
      placement="bottom-start"
      @click-outside="open = false"
    >
      <template #default="{ maxDimensions }">
        <section class="flex min-w-54 flex-col" :style="maxDimensions">
          <header class="mx-3 border-b border-neutral-200">
            <div
              class="flex w-full items-center justify-between space-x-4 py-3"
            >
              <h3 class="h3 whitespace-nowrap text-neutral-900">
                {{ filter.label }}
              </h3>
              <button
                class="flex size-4 items-center justify-center rounded-full"
                aria-label="Close"
                data-testid="close-dropdown-button"
                @click="open = false"
              >
                <div class="i-ph-x-bold size-4" />
              </button>
            </div>
            <div v-if="search" class="flex items-center justify-between pb-3">
              <Search
                v-model="searchInput"
                :focus="open"
                placeholder="Search..."
                type="search"
              />
            </div>
          </header>
          <div class="filter-item-container flex-1">
            <ul class="px-3 pt-2 pb-3">
              <li
                v-for="(item, index) in filteredItems"
                :key="item.value"
                :class="{
                  'mt-2':
                    index > 0 &&
                    item?.group &&
                    item?.group !== filteredItems[index - 1]?.group,
                }"
                :data-testid="
                  'filter-item-' +
                  item.label.toLocaleLowerCase().replaceAll(' ', '-')
                "
              >
                <h4
                  v-if="
                    item.group && item.group !== filteredItems[index - 1]?.group
                  "
                  class="text-sm font-medium"
                >
                  {{ item.group }}
                </h4>
                <div
                  :data-analytics="
                    dataAnalyticsTagMap && dataAnalyticsTagMap[item.value]
                  "
                >
                  <ListItemCheckbox
                    v-if="filter.isMultiple"
                    :checked="!!item.active"
                    @click="$emit('filter-change', { item, name: filter.name })"
                  >
                    {{
                      (item as FindManyFilterItem).display_name || item.label
                    }}
                  </ListItemCheckbox>
                  <ListItemRadio
                    v-else
                    :checked="!!item.active"
                    @click="$emit('filter-change', { item, name: filter.name })"
                  >
                    {{
                      (item as FindManyFilterItem).display_name || item.label
                    }}
                  </ListItemRadio>
                </div>
              </li>
            </ul>
          </div>
          <footer
            v-if="
              filter.isMultiple && (!selectAllDisabled || !clearAllDisabled)
            "
            class="flex justify-end border-t border-neutral-200 px-4 py-3"
          >
            <Button
              v-if="!selectAllDisabled"
              class="px-2 text-sm"
              variant="text"
              @click="$emit('filter-all', filter.name)"
            >
              Select all
            </Button>
            <Button
              v-if="!clearAllDisabled"
              class="px-2 text-sm"
              variant="text"
              @click="$emit('filter-clear', filter.name)"
            >
              Clear
            </Button>
          </footer>
        </section>
      </template>
    </FloatingUi>
  </div>
</template>
